/* ============================================================
   ADMIN APP — the /admin surface. Gates on auth + allow-list, then
   shows the dashboard or the section editor. Content/media come from
   ContentProvider (published, live); edits flow through useDrafts.
   ============================================================ */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ContentProvider, useContent, useSections } from '../content/ContentContext.jsx'
import { DEFAULT_LOCALE } from '../content/site.js'
import { useAuth } from './AuthContext.jsx'
import { useDrafts } from './useDrafts.js'
import { Login } from './Login.jsx'
import { Dashboard } from './Dashboard.jsx'
import { SectionEditor } from './SectionEditor.jsx'
import { SectionsManager } from './SectionsManager.jsx'
import { MediaLibrary } from './MediaLibrary.jsx'
import { Enquiries } from './Enquiries.jsx'
import { SettingsScreen } from './SettingsScreen.jsx'
import { useEnquiries } from './useEnquiries.js'
import { ConfirmDialog } from './ConfirmDialog.jsx'
import { Icon } from '../components/Icon.jsx'
import '../styles/admin.css'

const VIEW_TITLES = {
  dashboard: 'Dashboard',
  sections: 'Sections',
  media: 'Media library',
  enquiries: 'Enquiries',
  settings: 'Settings',
}

// Single source of truth for the nav, reused by the desktop sidebar and the
// mobile bottom bar. The dashboard tab also covers the section editor.
const NAV = [
  { name: 'dashboard', label: 'Dashboard', icon: 'grid', matchEditor: true },
  { name: 'sections', label: 'Sections', icon: 'layers' },
  { name: 'media', label: 'Media', icon: 'image' },
  { name: 'enquiries', label: 'Enquiries', icon: 'inbox', badge: true },
  { name: 'settings', label: 'Settings', icon: 'gauge' },
]

function isActive(item, view) {
  return view.name === item.name || (item.matchEditor && view.name === 'editor')
}

function Workspace() {
  const { content } = useContent() // published baseline (for discard) + media
  const sections = useSections()
  const { signOut, user } = useAuth()
  const drafts = useDrafts(DEFAULT_LOCALE, content, sections)
  const { unreadCount } = useEnquiries()
  const navigate = useNavigate()
  const [view, setView] = useState({ name: 'dashboard' })
  // Pending navigation held back by the unsaved-changes guard, or null.
  const [pendingNav, setPendingNav] = useState(null)

  // The editor has unpublished edits when its section's meta status is Draft.
  const editorDirty =
    view.name === 'editor' && (drafts.meta[view.key]?.status || 'Published') === 'Draft'

  // Route every navigation through this guard: if the editor is dirty, hold
  // the action and open the modal; otherwise run it immediately.
  const guardedNav = (action) => {
    if (editorDirty) setPendingNav(() => action)
    else action()
  }

  const goEdit = (key) => setView({ name: 'editor', key })
  const goDash = () => guardedNav(() => setView({ name: 'dashboard' }))
  const go = (name) => guardedNav(() => setView({ name }))

  // Native prompt for actual tab close / refresh while the editor is dirty
  // (browsers don't allow a custom modal here).
  useEffect(() => {
    if (!editorDirty) return
    const onBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [editorDirty])

  return (
    <div className="admin">
      <div className="admin-layout">
        <aside className="sidebar">
          <div className="smark">
            Curated<b>H</b>omes
          </div>
          <div className="navlabel">Workspace</div>
          {NAV.map((item) => (
            <button
              key={item.name}
              className={'navitem' + (isActive(item, view) ? ' active' : '')}
              onClick={() => go(item.name)}
            >
              <span className="ni">
                <Icon name={item.icon} size={18} />
              </span>
              {item.label}
              {item.badge && unreadCount > 0 && <span className="badge">{unreadCount}</span>}
            </button>
          ))}
          <div className="sfoot">
            <button className="viewlive" onClick={() => guardedNav(() => navigate('/'))}>
              <Icon name="external" size={15} /> View live site
            </button>
            <button className="navitem" style={{ marginTop: 8 }} onClick={signOut}>
              <span className="ni">
                <Icon name="back" size={18} />
              </span>
              Sign out
            </button>
            <div
              style={{
                fontFamily: 'var(--ff-mono)',
                fontSize: 10,
                color: 'var(--muted)',
                padding: '10px',
              }}
            >
              {user?.email}
            </div>
          </div>
        </aside>

        <div className="admin-main">
          {view.name === 'editor' ? (
            <SectionEditor sectionKey={view.key} drafts={drafts} onBack={goDash} />
          ) : (
            <>
              <div className="admin-topbar">
                <div>
                  <div className="crumb">BACKOFFICE</div>
                  <h1>{VIEW_TITLES[view.name] || 'Dashboard'}</h1>
                </div>
                <button
                  className="viewlive mob-only"
                  style={{ width: 'auto' }}
                  onClick={() => guardedNav(() => navigate('/'))}
                >
                  <Icon name="external" size={14} /> Live
                </button>
              </div>
              <div className="admin-body">
                {view.name === 'sections' ? (
                  <SectionsManager />
                ) : view.name === 'media' ? (
                  <MediaLibrary />
                ) : view.name === 'enquiries' ? (
                  <Enquiries />
                ) : view.name === 'settings' ? (
                  <SettingsScreen />
                ) : (
                  <Dashboard
                    meta={drafts.meta}
                    onEdit={goEdit}
                    unreadCount={unreadCount}
                    onView={(name) => setView({ name })}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile bottom navigation — the sidebar is hidden below 860px. */}
      <nav className="bottom-nav">
        {NAV.map((item) => (
          <button
            key={item.name}
            className={'bnav' + (isActive(item, view) ? ' active' : '')}
            onClick={() => go(item.name)}
          >
            <span className="bnav-ic">
              <Icon name={item.icon} size={20} />
              {item.badge && unreadCount > 0 && <span className="bnav-dot"></span>}
            </span>
            {item.label}
          </button>
        ))}
      </nav>

      <ConfirmDialog
        open={!!pendingNav}
        title="Unpublished changes"
        message="You have edits that haven't been published. Leave without publishing? Your draft is kept — you can come back and publish it later."
        confirmLabel="Leave"
        cancelLabel="Keep editing"
        onConfirm={() => {
          const action = pendingNav
          setPendingNav(null)
          action?.()
        }}
        onCancel={() => setPendingNav(null)}
      />
    </div>
  )
}

export default function AdminApp() {
  const { loading, checked, user, isAdmin } = useAuth()
  // Wait for both the auth state AND (when signed in) the admin allow-list
  // check to resolve, so the refusal screen never flashes mid-check.
  if (loading || (user && !checked))
    return (
      <div className="login">
        <div className="login-hint">Loading…</div>
      </div>
    )
  if (!user || !isAdmin) return <Login />
  return (
    <ContentProvider>
      <Workspace />
    </ContentProvider>
  )
}
